import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AuthLayout } from "@/components/Brand";
import { Field, FormError, SubmitButton, LoadingState } from "@/components/common";
import { useAuth, homePathFor } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";

const strength = (p) => (p.length >= 12 && /[A-Z]/.test(p) && /\d/.test(p) && /[^A-Za-z0-9]/.test(p) ? 3 : p.length >= 8 && /[A-Za-z]/.test(p) && /\d/.test(p) ? 2 : p ? 1 : 0);

export default function AccountSecurity() {
  const { me, refresh } = useAuth();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (me === undefined) return <LoadingState full />;
  if (!me) return <Navigate to="/login" replace />;
  const forced = me.user.must_change_password;
  const s = strength(f.new_password);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return setError(t("b8.account.mismatch"));
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success(t("b8.account.changed"));
      setF({ current_password: "", new_password: "", confirm: "" });
      await refresh();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const revoke = async () => {
    try {
      await api.post("/auth/revoke-sessions");
      toast.success(t("b8.account.revoked"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <AuthLayout title={t("b8.account.title")} subtitle={forced ? t("b8.account.forced") : me.user.email}>
      <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
        {forced && <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="temp-password-notice">{t("b8.account.forced")}</p>}
        <FormError message={error} />
        <Field label={t("b8.account.current")} htmlFor="cp-cur"><Input id="cp-cur" type="password" required autoComplete="current-password" value={f.current_password} onChange={set("current_password")} data-testid="current-password-input" /></Field>
        <Field label={t("b8.account.new")} htmlFor="cp-new" hint={t("auth.passwordRule")}>
          <Input id="cp-new" type="password" required minLength={8} autoComplete="new-password" value={f.new_password} onChange={set("new_password")} data-testid="new-password-input" />
          <div className="mt-2 flex gap-1" data-testid="password-strength" data-strength={s}>{[1, 2, 3].map((i) => <span key={i} className={`h-1.5 flex-1 rounded ${s >= i ? ["", "bg-red-500", "bg-amber-500", "bg-emerald-500"][s] : "bg-slate-200"}`} />)}</div>
        </Field>
        <Field label={t("b8.account.confirm")} htmlFor="cp-conf"><Input id="cp-conf" type="password" required value={f.confirm} onChange={set("confirm")} data-testid="confirm-password-input" /></Field>
        <SubmitButton busy={busy} disabled={s < 2} className="w-full" data-testid="change-password-submit">{t("b8.account.change")}</SubmitButton>
      </form>
      {!forced && (
        <div className="mt-8 space-y-3 border-t border-slate-200 pt-6">
          <p className="text-sm text-slate-600">{t("b8.account.revokeHint")}</p>
          <Button variant="outline" onClick={revoke} data-testid="revoke-sessions-button">{t("b8.account.revoke")}</Button>
          <p><Link to={homePathFor(me)} className="text-sm font-semibold text-teal-700" data-testid="account-back-link">← {t("landing.openApp")}</Link></p>
        </div>
      )}
    </AuthLayout>
  );
}
